import React, { useState } from 'react'

import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

type EntityWithId = {
  id: number
}

type FormDialogProps<T> = {
  open: boolean
  entity?: T
  entityLabel: string
  onClose: () => void
  children: React.ReactElement<{ saveTrigger?: number }>
}
function FormDialog<T extends EntityWithId>(props: FormDialogProps<T>) {
  const { children, entity, entityLabel, onClose, open } = props
  const theme = useTheme()
  // on phones the dialog takes the whole screen so long forms aren't squeezed between margins
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // used to remotely trigger the save of the child form
  const [saveTrigger, setSaveTrigger] = useState(0)

  const handleSave = () => {
    setSaveTrigger((prev) => prev + 1)
    // wait a second and reinitialize the save trigger
    setTimeout(() => {
      setSaveTrigger(0)
    }, 1000)
  }

  return (
    <Dialog fullWidth maxWidth="md" fullScreen={fullScreen} open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Box sx={{ mr: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" noWrap={fullScreen}>
              {entity ? 'Edit' : 'Add'} {entityLabel}
              {entity && !fullScreen ? ` (ID: ${entity.id})` : ''}
            </Typography>
            {entity && fullScreen && (
              <Typography variant="caption" component="div" color="grey.400">
                ID: {entity.id}
              </Typography>
            )}
          </Box>
          <Button autoFocus color="inherit" onClick={handleSave}>
            Save
          </Button>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ px: { zero: 2, sm: 3 } }}>
        {React.isValidElement(children) && React.cloneElement(children, { saveTrigger })}
      </DialogContent>
    </Dialog>
  )
}
export default FormDialog
